import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API from "../lib/api";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { Heart, ShoppingBag, Star, ArrowLeft, Minus, Plus, Trash } from "@phosphor-icons/react";
import { Separator } from "../components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart, toggleWishlist, isInWishlist } = useCart();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setLoading(true);
    API.get(`/products/${id}`)
      .then(r => {
        setProduct(r.data);
        setReviews(r.data.reviews || []);
      })
      .catch(err => {
        console.error("Failed to load product:", err);
        navigate("/shop");
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const handleAddToCart = async () => {
    if (!user || user === false) { navigate("/auth"); return; }
    await addToCart(product.id, quantity);
  };

  const handleWishlist = async () => {
    if (!user || user === false) { navigate("/auth"); return; }
    await toggleWishlist(product.id);
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try {
      await API.delete(`/reviews/${reviewId}`);
      setReviews(prev => prev.filter(r => r.id !== reviewId));
    } catch (err) {
      console.error("Failed to delete review:", err);
    }
  };

  const handleReview = async (e) => {
    e.preventDefault();
    if (!user || user === false) { navigate("/auth"); return; }
    setSubmitting(true);
    try {
      const { data } = await API.post("/reviews", { product_id: product.id, ...reviewForm });
      setReviews(prev => [data, ...prev]);
      setReviewForm({ rating: 5, comment: "" });
    } catch (err) {
      console.error("Failed to submit review:", err);
    }
    setSubmitting(false);
  };

  const avgRating = reviews.length > 0 ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : "0";

  if (loading) {
    return (
      <div className="min-h-screen pt-28 pb-24 px-6 lg:px-12 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div className="aspect-square bg-[#121212] animate-pulse" />
          <div className="space-y-6">
            <div className="h-8 bg-[#121212] animate-pulse w-3/4" />
            <div className="h-4 bg-[#121212] animate-pulse w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) return null;
  const inWishlist = isInWishlist(product.id);

  return (
    <div className="min-h-screen pt-28 pb-24 px-6 lg:px-12 max-w-7xl mx-auto" data-testid="product-detail-page">
      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-[#A1A1AA] hover:text-[#D4AF37] transition-colors duration-300 text-sm mb-10 font-light"
        data-testid="back-button"
      >
        <ArrowLeft size={16} weight="thin" /> Back
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
        {/* Images */}
        <div className="space-y-4">
          <div className="aspect-square overflow-hidden bg-[#0a0a0a] border border-[rgba(212,175,55,0.1)]">
            <img
              src={product.images?.[selectedImage] || product.image_url}
              alt={product.name}
              className="w-full h-full object-cover"
              data-testid="product-main-image"
            />
          </div>
          {product.images?.length > 1 && (
            <div className="flex gap-3">
              {product.images.map((img, i) => (
                <button
                  key={`thumb-${img}-${i}`}
                  onClick={() => setSelectedImage(i)}
                  className={`w-20 h-20 overflow-hidden border transition-all duration-300 ${
                    i === selectedImage ? "border-[#D4AF37]" : "border-white/10 hover:border-white/20"
                  }`}
                  data-testid={`thumbnail-${i}`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="animate-slide-in-right" style={{ opacity: 0 }}>
          <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-3 font-medium">
            {product.category?.replace("-", " ")}
          </p>
          <h1 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light text-[#FDFBF7] tracking-tighter mb-4" data-testid="product-name">
            {product.name}
          </h1>

          {reviews.length > 0 && (
            <div className="flex items-center gap-2 mb-6">
              <div className="flex">
                {[1,2,3,4,5].map(s => (
                  <Star key={s} size={14} weight={s <= Math.round(avgRating) ? "fill" : "regular"} className="text-[#D4AF37]" />
                ))}
              </div>
              <span className="text-xs text-[#A1A1AA] font-light">{avgRating} ({reviews.length} reviews)</span>
            </div>
          )}

          {!product.coming_soon && (
            <p className="text-3xl text-[#D4AF37] font-['Cormorant_Garamond'] font-light mb-8" data-testid="product-price">
              ${product.price?.toFixed(2)}
            </p>
          )}

          <Separator className="bg-white/5 mb-8" />

          <p className="text-[#A1A1AA] text-sm leading-relaxed font-light mb-8" data-testid="product-description">
            {product.description}
          </p>

          {product.scent_notes && (
            <div className="mb-8">
              <p className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA] mb-2 font-medium">Scent Notes</p>
              <p className="text-[#FDFBF7] text-sm font-light">{product.scent_notes}</p>
            </div>
          )}

          {product.size && (
            <div className="mb-8">
              <p className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA] mb-2 font-medium">Size</p>
              <p className="text-[#FDFBF7] text-sm font-light">{product.size}</p>
            </div>
          )}

          {!product.coming_soon && (
            <>
              {/* Quantity */}
              <div className="flex items-center gap-4 mb-8">
                <span className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA] font-medium">Qty</span>
                <div className="flex items-center border border-white/10">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="px-3 py-2 text-[#A1A1AA] hover:text-[#FDFBF7] transition-colors"
                    data-testid="qty-decrease"
                  >
                    <Minus size={14} weight="thin" />
                  </button>
                  <span className="px-4 py-2 text-sm text-[#FDFBF7] min-w-[40px] text-center font-light" data-testid="qty-display">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="px-3 py-2 text-[#A1A1AA] hover:text-[#FDFBF7] transition-colors"
                    data-testid="qty-increase"
                  >
                    <Plus size={14} weight="thin" />
                  </button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-4">
                <button
                  onClick={handleAddToCart}
                  className="btn-luxury flex-1 flex items-center justify-center gap-3 bg-[#D4AF37] text-black py-4 text-sm tracking-[0.15em] uppercase font-medium hover:bg-[#E5C158] transition-colors duration-500"
                  data-testid="add-to-cart-button"
                >
                  <ShoppingBag size={18} weight="bold" /> Add to Bag
                </button>
                <button
                  onClick={handleWishlist}
                  className={`w-14 flex items-center justify-center border transition-all duration-300 ${
                    inWishlist ? "border-[#D4AF37] text-[#D4AF37]" : "border-white/10 text-[#A1A1AA] hover:border-[#D4AF37] hover:text-[#D4AF37]"
                  }`}
                  data-testid="wishlist-button"
                >
                  <Heart size={20} weight={inWishlist ? "fill" : "thin"} />
                </button>
              </div>
            </>
          )}

          {product.coming_soon && (
            <div className="border border-[#D4AF37] p-6 text-center">
              <p className="text-[#D4AF37] text-sm tracking-[0.15em] uppercase font-medium">Coming Soon</p>
              <p className="text-[#A1A1AA] text-xs mt-2 font-light">This collection is launching soon. Stay tuned.</p>
            </div>
          )}

          {/* Stock indicator */}
          {!product.coming_soon && product.stock > 0 && product.stock <= 10 && (
            <p className="text-xs text-[#D4AF37] mt-4 font-light">Only {product.stock} left in stock</p>
          )}
        </div>
      </div>

      {/* Reviews Tab Section */}
      <div className="mt-24">
        <Tabs defaultValue="reviews" className="w-full">
          <TabsList className="bg-transparent border-b border-white/5 rounded-none w-full justify-start gap-8 h-auto pb-0">
            <TabsTrigger value="reviews" className="bg-transparent text-[#A1A1AA] data-[state=active]:text-[#D4AF37] data-[state=active]:shadow-none rounded-none border-b-2 border-transparent data-[state=active]:border-[#D4AF37] pb-3 text-xs uppercase tracking-[0.15em] font-medium">
              Reviews ({reviews.length})
            </TabsTrigger>
            <TabsTrigger value="details" className="bg-transparent text-[#A1A1AA] data-[state=active]:text-[#D4AF37] data-[state=active]:shadow-none rounded-none border-b-2 border-transparent data-[state=active]:border-[#D4AF37] pb-3 text-xs uppercase tracking-[0.15em] font-medium">
              Details
            </TabsTrigger>
          </TabsList>

          <TabsContent value="reviews" className="mt-8">
            {/* Write review */}
            {user && user !== false && !product.coming_soon && (
              <form onSubmit={handleReview} className="mb-12 p-8 bg-[#121212] border border-[rgba(212,175,55,0.1)]" data-testid="review-form">
                <h3 className="font-['Cormorant_Garamond'] text-xl font-light text-[#FDFBF7] mb-6">Write a Review</h3>
                <div className="flex gap-2 mb-4">
                  {[1,2,3,4,5].map(s => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setReviewForm(p => ({ ...p, rating: s }))}
                      className="transition-colors"
                      data-testid={`rating-star-${s}`}
                    >
                      <Star size={20} weight={s <= reviewForm.rating ? "fill" : "regular"} className="text-[#D4AF37]" />
                    </button>
                  ))}
                </div>
                <textarea
                  value={reviewForm.comment}
                  onChange={e => setReviewForm(p => ({ ...p, comment: e.target.value }))}
                  placeholder="Share your experience..."
                  className="w-full bg-[#0a0a0a] border border-white/10 p-4 text-sm text-[#FDFBF7] placeholder-zinc-500 focus:outline-none focus:border-[#D4AF37]/40 transition-colors duration-300 font-light min-h-[100px]"
                  data-testid="review-comment-input"
                />
                <button
                  type="submit"
                  disabled={submitting || !reviewForm.comment}
                  className="mt-4 bg-[#D4AF37] text-black px-8 py-3 text-xs tracking-[0.15em] uppercase font-medium hover:bg-[#E5C158] transition-colors duration-300 disabled:opacity-50"
                  data-testid="submit-review-btn"
                >
                  {submitting ? "Submitting..." : "Submit Review"}
                </button>
              </form>
            )}

            {/* Reviews list */}
            {reviews.length === 0 ? (
              <p className="text-[#A1A1AA] text-sm font-light py-8">No reviews yet. Be the first to share your experience.</p>
            ) : (
              <div className="space-y-6">
                {reviews.map(review => (
                  <div key={review.id} className="pb-6 border-b border-white/5" data-testid={`review-${review.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-[#FDFBF7] font-light">{review.user_name}</span>
                        <div className="flex">
                          {[1,2,3,4,5].map(s => (
                            <Star key={s} size={12} weight={s <= review.rating ? "fill" : "regular"} className="text-[#D4AF37]" />
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-[#A1A1AA] font-light">
                          {new Date(review.created_at).toLocaleDateString()}
                        </span>
                        {user && user !== false && (user.role === "admin" || review.user_id === user.id) && (
                          <button
                            onClick={() => handleDeleteReview(review.id)}
                            className="text-[#A1A1AA] hover:text-red-400 transition-colors"
                            title="Delete review"
                            data-testid={`delete-review-${review.id}`}
                          >
                            <Trash size={14} weight="thin" />
                          </button>
                        )}
                      </div>
                    </div>
                    <p className="text-[#A1A1AA] text-sm font-light">{review.comment}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="details" className="mt-8">
            <div className="space-y-4 text-sm text-[#A1A1AA] font-light">
              {product.scent_notes && <p><span className="text-[#FDFBF7]">Scent Notes:</span> {product.scent_notes}</p>}
              {product.size && <p><span className="text-[#FDFBF7]">Size:</span> {product.size}</p>}
              <p><span className="text-[#FDFBF7]">Category:</span> {product.category?.replace("-", " ")}</p>
              <p><span className="text-[#FDFBF7]">Availability:</span> {product.coming_soon ? "Coming Soon" : product.stock > 0 ? "In Stock" : "Out of Stock"}</p>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
